import {
  PROMPT_OVERVIEW_TAB_PARAM,
  PromptOverviewTab,
} from "@/components/PromptTemplate/OverviewContent/utils/promptVersionLogsReturnNavigation";
import type { StructuredSearchStore } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import { REQUESTS_SIDEBAR_URL_NAMESPACE } from "@/components/StructuredSearch/hooks/structuredSearchUrlNamespaces";
import { applyStructuredSearchStateToSearchParams } from "@/components/StructuredSearch/hooks/useStructuredSearchURLSync";
import {
  appendReviewLogsDrawerIntent,
  appendReviewLogsReturnTo,
  buildReviewLogsReturnTo,
  REQUEST_LOG_SOURCE_PARAM,
  REQUEST_LOG_SOURCE_REQUESTS_ANALYTICS,
  stripReviewLogsDrawerIntent,
} from "@/utils/requestLogUrlParams";
import {
  isWorkspacePromptVersionPathname,
  shouldPersistStructuredSearchReturnTo,
} from "@/utils/workspacePathMatchers";

const toSearch = (params: URLSearchParams): string => {
  const value = params.toString();
  return value ? `?${value}` : "";
};

export const buildRequestNavigationSearch = ({
  pathname,
  locationSearch,
  structuredSearchStore,
  trackingSurface,
}: {
  pathname: string;
  locationSearch: string;
  structuredSearchStore: StructuredSearchStore;
  trackingSurface?: "requests_analytics";
}): string => {
  const fromPrompt = isWorkspacePromptVersionPathname(pathname);
  // Always start clean: request-detail owns the sidebar namespace only.
  // Table/prompt filters stay in returnTo so later prompt navigation cannot migrate them.
  const params = applyStructuredSearchStateToSearchParams(
    new URLSearchParams(),
    {
      // Include locked filters so opening a request from the Analytics grid hydrates the sidebar
      // with the full prompt-page filter set (including the locked prompt/version filter).
      filters: structuredSearchStore.filters,
      dateRange: structuredSearchStore.dateRange,
      q: structuredSearchStore.q,
      pageIndex: structuredSearchStore.pageIndex,
      pageSize: structuredSearchStore.pageSize,
      sorting: structuredSearchStore.sorting,
    },
    { namespace: REQUESTS_SIDEBAR_URL_NAMESPACE },
  );

  if (trackingSurface) {
    params.set(REQUEST_LOG_SOURCE_PARAM, REQUEST_LOG_SOURCE_REQUESTS_ANALYTICS);
  }

  let search = toSearch(params);

  if (fromPrompt) {
    const returnParams = new URLSearchParams(
      stripReviewLogsDrawerIntent(locationSearch).replace(/^\?/, ""),
    );
    returnParams.set(PROMPT_OVERVIEW_TAB_PARAM, PromptOverviewTab.Analytics);
    search = appendReviewLogsReturnTo(search, `${pathname}${toSearch(returnParams)}`);
  } else if (shouldPersistStructuredSearchReturnTo(pathname)) {
    // Return to the table URL (table namespace), not the sidebar-prefilled request URL.
    search = appendReviewLogsReturnTo(search, buildReviewLogsReturnTo(pathname, locationSearch));
  }

  return appendReviewLogsDrawerIntent(search);
};
